import { inject, Injectable } from '@angular/core';
import * as ActionCable from '@rails/actioncable';
import { v4 as uuidv4 } from 'uuid';

import { Cable } from './cable';
import { CameraState, WebRTCCamera } from './camera';
import DetectRTC from '../utils/DetectRTC';

@Injectable({ providedIn: 'root' })
export class SystemCheck {
  browserName: string;
  microphone = false;
  speakers = false;
  webrtc = false;
  graphicsAcceleration = false;
  isConnected = false;
  pingChannel: ActionCable.Channel;

  private cable = inject(Cable);
  private camera = new WebRTCCamera(640, 480);

  get browser(): boolean {
    switch (this.browserName) {
      case 'chrome':
      case 'opera':
      case 'firefox':
        return true;
      case 'safari':
        return this.browserVersion.version >= 11;
      default:
        return false;
    }
  }

  get browserVersion() {
    return DetectRTC.browser;
  }

  get hasGraphicsAcceleration(): boolean {
    return this.graphicsAcceleration;
  }

  start(): Promise<void> {
    return new Promise((resolve) => {
      DetectRTC.load(() => {
        this.browserName = DetectRTC.browser.name.toLowerCase();
        this.microphone = DetectRTC.hasMicrophone;
        this.speakers = DetectRTC.hasSpeakers || this.browserName === 'firefox';
        this.webrtc = DetectRTC.isWebRTCSupported;
        this.graphicsAcceleration = this.detectGraphicsAcceleration();
        resolve();
      });
    });
  }

  stop() {
    this.pingChannel?.unsubscribe();
  }

  testWebsockets(): Promise<boolean> {
    return new Promise((resolve) => {
      const uuid = uuidv4();
      const params = {
        channel: 'PingChannel',
        uuid: uuid
      };
      this.pingChannel = this.cable.consumer.subscriptions.create(params, {
        connected: () => {
          this.isConnected = true;
          this.pingChannel.perform('ping', { uuid: uuid });
        },
        disconnected: () => (this.isConnected = false),
        received: (data: any) => {
          resolve(data.pong && data.uuid === uuid);
        }
      });
    });
  }

  testWebcam(): Promise<boolean> {
    return new Promise((resolve) => {
      if (DetectRTC.hasWebcam) {
        this.camera.activate().then(
          (stream: MediaStream) => {
            stream.getTracks().forEach((track) => track.stop());
            this.camera.state = CameraState.Activated;
            resolve(true);
          },
          (err) => {
            console.error('failed activating camera', err);
            this.camera.state = CameraState.Deactivated;
            resolve(false);
          }
        );
      } else {
        this.camera.state = CameraState.Uninitialized;
        resolve(false);
      }
    });
  }

  private detectGraphicsAcceleration(): boolean {
    try {
      const webglContext = this.createWebGLContext();
      if (!webglContext) {
        return false;
      }

      return this.isHardwareAccelerated(webglContext);
    } catch (error) {
      console.warn('Failed to detect graphics acceleration:', error);
      return false;
    }
  }

  /**
   * Support methods to detect graphics acceleration
   */
  private createWebGLContext(): WebGLRenderingContext | null {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return gl ? <WebGLRenderingContext>gl : null;
  }

  private isHardwareAccelerated(webglContext: WebGLRenderingContext): boolean {
    const debugInfo = webglContext.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) {
      return true; // Default to true if we can't determine renderer info
    }

    const renderer = webglContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    if (!renderer || typeof renderer !== 'string') {
      return true; // Default to true if renderer info is unavailable
    }

    const softwareRenderers = ['software', 'mesa', 'llvmpipe', 'swiftshader', 'microsoft basic render driver'];

    const rendererLower = renderer.toLowerCase();
    const isSoftwareRenderer = softwareRenderers.some((sw) => rendererLower.includes(sw));

    return !isSoftwareRenderer;
  }
  /**
   * Support methods to detect graphics acceleration - END
   */
}
